import {
  Table,
  Space,
  Tag,
  Button,
  Input,
  TableProps,
  Statistic,
  Popover,
  Badge,
  List,
  Typography,
  Tooltip,
  Result,
  Dropdown,
  message,
  theme,
} from "antd";
import type { MenuProps } from "antd";
import type { TablePaginationConfig } from "antd/es/table";
import type {
  FilterValue,
  SorterResult,
  SortOrder,
  TableCurrentDataSource,
} from "antd/es/table/interface";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import styles from "./BusinessTable.module.css";
import {
  getFiscalizationConfigurationThunk,
  getBusinessesThunk,
  getBusinessThunk,
  setCurrentBusiness,
  selectTotalBusinessCount,
  selectLastUpdateKey,
} from "../../businessesSlice";
import {
  selectIdToken,
  selectIsKlearlyAdmin,
  selectUser,
} from "../../../access-control/accountSlice";
import BusinessDetailsModal from "../Business-Details-Modal/BusinessDetailsModal";
import BusinessEditorModal from "../Business-Editor-Modal/BusinessEditor";
import CountryFlag from "../../../common/country-flag/CountryFlag";
import { Empty, Segmented } from "antd";
import {
  CheckCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  SearchOutlined,
  FlagOutlined,
  InfoCircleOutlined,
  EditOutlined,
  FileOutlined,
  HddOutlined,
  ExportOutlined,
  MoreOutlined,
  DesktopOutlined,
  PlusCircleOutlined,
  CopyOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import ApprovalModal from "../Approval-Modal/ApprovalModal";
import DeclinedModal from "../Declined-Modal/DeclinedModal";
import PendingVerificationModal from "../Pending-Verification-Modal/PendingVerificationModal";
import Loader from "../../../common/loader/Loader";
import {
  BusinessFeatureFilter,
  ComplianceStatus,
  IBusinessFull,
  IBusinessFullQuery,
  IOnboardingFlag,
  OnlinePaymentStatus,
} from "../../../../interfaces/businessFull.interface";
import OnboardingFlagsList from "./OnboardingFlagsList";
import { mergeOnboardingFlags } from "./onboardingFlagAttribution.helpers";
import AcquirerStatusCell, {
  AcquirerStatusView,
} from "./components/AcquirerStatusCell";
import { resolveAcquirerColumnStatus } from "../../utils/resolveAcquirerColumnStatus";
import SubmitModal from "../Submit-Modal/SubmitModal";
import { TableParams } from "../../../../interfaces/tableParams.interface";
import { IBucketDeal } from "../../../../interfaces/bucketDeal.interface";
import { IPagination } from "../../../../interfaces/pagination.interface";
import debounce from "lodash/debounce";
import BusinessFilesModal from "../Business-Files-Modal/BusinessFilesModal";
import {
  getPartnersThunk,
  selectPartners,
} from "../../../partners/partnersSlice";
import { IPartner } from "../../../../interfaces/partner.interface";
import BusinessTableFilters from "./components/BusinessTableFilters";
import SearchHintContent from "./components/SearchHintContent";
import BusinessDevicesModal from "../Business-Devices-Modal/BusinessDevicesModal";
import AdyenSubmitModal from "../Adyen-Submit-Modal/AdyenSubmitModal";
import Shift4SubmitModal from "../Shift4-Submit-Modal/Shift4SubmitModal";
import { selectHideUnreleasedAcquirers } from "../../../feature-flags/featureFlagsSlice";
import { resolveCountryAlpha2 } from "../../utils/resolveCountryAlpha2";
import { WEB_PORTAL } from "../../../../config";
import { LocalDateAsUTC } from "../../../../utils/LocalDateAsUTC";
import { resolveBusinessSortParam } from "../../utils/resolveBusinessSortParam";
import {
  BusinessSortField,
  isBusinessSortField,
} from "../../utils/businessSortField";
import { useSearchParams } from "react-router-dom";
import {
  readBusinessFiltersFromParams,
  readBusinessPaginationFromParams,
  BUSINESS_PAGE_SIZE_OPTIONS,
  buildBusinessSearchParams,
  pageAfterSizeChange,
} from "../../utils/businessFilterParams";
import BusinessColumnSettings from "./components/BusinessColumnSettings";
import {
  applyBusinessColumnPrefs,
  buildBusinessColumnOptions,
  BusinessColumnPrefs,
  clearBusinessColumnPrefs,
  EMPTY_BUSINESS_COLUMN_PREFS,
  loadBusinessColumnPrefs,
  saveBusinessColumnPrefs,
} from "../../utils/businessColumnPrefs";
const { Column, ColumnGroup } = Table;

const COPY_ELIGIBLE_ADYEN_STATUSES: ReadonlySet<OnlinePaymentStatus> =
  new Set<OnlinePaymentStatus>([
    "initiated",
    "awaitingMerchantAction",
    "awaitingData",
    "pendingToS",
    "pending",
    "verificationPending",
    "verified",
  ]);

interface IBusinessTableProps {
  /** Extra row actions appended to the row menu; used by the QA tools page. */
  extraRowActions?: Array<{
    key: string;
    label: string;
    icon?: React.ReactNode;
    onClick: (business: IBusinessFull) => void;
  }>;
}

export default function BusinessTable({
  extraRowActions,
}: IBusinessTableProps) {
  const { token } = theme.useToken();
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [isEditorModalVisible, setIsEditorModalVisible] = useState(false);
  const [isFilesModalVisible, setIsFilesModalVisible] = useState(false);
  const [isDevicesModalVisible, setIsDevicesModalVisible] = useState(false);
  const [isApprovalModalVisible, setIsApprovalModalVisible] = useState(false);
  const [isDeclinedModalVisible, setIsDeclinedModalVisible] = useState(false);
  const [isSubmitModalVisible, setIsSubmitModalVisible] = useState(false);
  const [isAdyenSubmitModalVisible, setIsAdyenSubmitModalVisible] =
    useState(false);
  const [adyenSubmitBusiness, setAdyenSubmitBusiness] =
    useState<IBusinessFull | null>(null);
  const [isShift4SubmitModalVisible, setIsShift4SubmitModalVisible] =
    useState(false);
  const [shift4SubmitBusiness, setShift4SubmitBusiness] =
    useState<IBusinessFull | null>(null);
  const [websiteFocusSignal, setWebsiteFocusSignal] = useState(0);
  const [
    isPendingVerificationModalVisible,
    setIsPendingVerificationdModalVisible,
  ] = useState(false);
  const [loading, setLoading] = useState(false);
  const lastUpdateKey = useAppSelector(selectLastUpdateKey);
  const partners = useAppSelector(selectPartners);
  const idToken = useAppSelector(selectIdToken);
  const isKlearlyAdmin = useAppSelector(selectIsKlearlyAdmin);
  const hideUnreleasedAcquirers = useAppSelector(selectHideUnreleasedAcquirers);
  const [statusView, setStatusView] = useState<AcquirerStatusView>("labels");
  const user = useAppSelector(selectUser);
  const canAccessMerchantHub = isKlearlyAdmin;
  const [businessesInTable, setBusinessesInTable] = useState<IBusinessFull[]>(
    []
  );
  // Sortable column keys advertised by getAll; the header is only clickable for
  // fields present here (empty until the first response, so no sort by default).
  const [sortableFields, setSortableFields] = useState<BusinessSortField[]>([]);
  const totalCount = useAppSelector(selectTotalBusinessCount);
  const [searchParams, setSearchParams] = useSearchParams();
  const [tableParams, setTableParams] = useState<TableParams>(() => {
    const { current, pageSize } =
      readBusinessPaginationFromParams(searchParams);
    return { pagination: { current, pageSize } };
  });
  const [filters, setFilters] = useState<Partial<IBusinessFullQuery>>(() =>
    readBusinessFiltersFromParams(searchParams)
  );
  const [searchValue, setSearchValue] = useState<string>(
    () => (filters.name as string) || ""
  );
  const [columnPrefs, setColumnPrefs] = useState<BusinessColumnPrefs>(
    loadBusinessColumnPrefs
  );
  const [searchHintOpen, setSearchHintOpen] = useState(false);
  const [searchHintWidth, setSearchHintWidth] = useState<number>();
  const searchInputWrapperRef = useRef<HTMLDivElement>(null);
  const openSearchHint = () => {
    if (searchInputWrapperRef.current) {
      setSearchHintWidth(
        searchInputWrapperRef.current.getBoundingClientRect().width
      );
    }
    setSearchHintOpen(true);
  };

  // Keep the hint popover width in sync with the input while open, so window
  // resizes don't leave the popover stranded at its open-time width.
  useEffect(() => {
    if (!searchHintOpen || !searchInputWrapperRef.current) return;
    const node = searchInputWrapperRef.current;
    const observer = new ResizeObserver(() => {
      setSearchHintWidth(node.getBoundingClientRect().width);
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [searchHintOpen]);
  const dispatch = useAppDispatch();

  const getActiveDeviceCount = (business: IBusinessFull): number =>
    business?.devices?.filter(
      (device) =>
        !!device?.lastOnline &&
        new LocalDateAsUTC(device.lastOnline).time >
          new LocalDateAsUTC().subtractMinutes(30).time
    ).length || 0;
  const featureTagProps: Record<
    BusinessFeatureFilter,
    {
      label: string;
      backgroundColor: string;
      borderColor: string;
      textColor: string;
    }
  > = {
    [BusinessFeatureFilter.Online]: {
      label: "Pay At Table",
      backgroundColor: "#e6f4ff",
      borderColor: "#91caff",
      textColor: "#1d39c4",
    },
    [BusinessFeatureFilter.PayByQR]: {
      label: "Pay By QR",
      backgroundColor: "#e6fffb",
      borderColor: "#87e8de",
      textColor: "#08979c",
    },
    [BusinessFeatureFilter.Tips]: {
      label: "Tips",
      backgroundColor: "#ede9fe",
      borderColor: "#c4b5fd",
      textColor: "#5b21b6",
    },
    [BusinessFeatureFilter.Deposits]: {
      label: "Deposits",
      backgroundColor: "#f0f5ff",
      borderColor: "#adc6ff",
      textColor: "#1d39c4",
    },
    [BusinessFeatureFilter.Reviews]: {
      label: "Reviews",
      backgroundColor: "#faf5ff",
      borderColor: "#e9d5ff",
      textColor: "#6d28d9",
    },
    [BusinessFeatureFilter.Cash]: {
      label: "Cash",
      backgroundColor: "#f0f5ff",
      borderColor: "#d6e4ff",
      textColor: "#2f54eb",
    },
    [BusinessFeatureFilter.Insights]: {
      label: "Insights",
      backgroundColor: "#fffbe6",
      borderColor: "#ffe58f",
      textColor: "#ad6800",
    },
  };

  const fetchAllBusinesses = async (params: {
    pagination: IPagination;
    filters: Partial<IBusinessFullQuery>;
  }) => {
    setLoading(true);
    const { sortBy, sortOrder } = resolveBusinessSortParam(
      tableParams.sortField,
      tableParams.sortOrder,
      sortableFields
    );
    await dispatch(
      getBusinessesThunk({
        currentPage: params?.pagination?.current!,
        pageSize: params?.pagination?.pageSize!,
        ...params.filters,
        sortBy,
        sortOrder,
      })
    )
      .unwrap()
      .then(({ data, totalCount, sortableFields: nextSortableFields }) => {
        setSortableFields(
          (nextSortableFields ?? []).filter(isBusinessSortField)
        );
        setBusinessesInTable(
          data.map((b) => ({
            ...b,
            key: b._id,
          }))
        );

        // Functional update so an in-flight fetch can't clobber a newer
        // current/pageSize that the user set while the request was pending.
        setTableParams((prev) => ({
          ...prev,
          pagination: {
            ...prev.pagination,
            total: totalCount,
          },
        }));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchPartners = async () => {
    if (partners?.length === 0) await dispatch(getPartnersThunk());
  };
  useEffect(() => {
    fetchAllBusinesses({
      pagination: {
        current: tableParams.pagination?.current || 1,
        pageSize: tableParams.pagination?.pageSize!,
      },
      filters,
    });
    fetchPartners();
    // Key the refetch on the pagination fields that should trigger it, not the
    // whole tableParams object — fetchAllBusinesses writes `total` back into
    // tableParams, and depending on the whole object re-fired this effect (and
    // re-issued getAll) on every fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize,
    tableParams.sortField,
    tableParams.sortOrder,
    JSON.stringify(filters),
    lastUpdateKey,
  ]);

  // Mirror active filters + pagination into the URL so the view is shareable and
  // the Export action reads the same filters. See ../../utils/businessFilterParams.
  useEffect(() => {
    const nextParams = buildBusinessSearchParams(
      filters,
      tableParams.pagination
    );
    setSearchParams(nextParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    JSON.stringify(filters),
    tableParams.pagination?.current,
    tableParams.pagination?.pageSize,
  ]);

  useEffect(() => {
    dispatch(getFiscalizationConfigurationThunk());
  }, [dispatch]);

  const handleCopyMerchantHubLink = async (record: IBusinessFull) => {
    // Copy the auth-gated portal link; it mints a fresh Adyen link on open.
    // Never copy the raw adyen.onboardingLink — it expires ~4 min after minting.
    const url = new URL("/adyen-onboarding", WEB_PORTAL.BASE_URL);
    url.searchParams.set("businessId", record._id);
    try {
      await navigator.clipboard.writeText(url.toString());
      message.success("Link copied!");
    } catch (err) {
      console.error("Clipboard write failed", err);
      message.error("Could not copy to clipboard");
    }
  };

  const showBusinessDetailsModal = (business: IBusinessFull) => {
    dispatch(setCurrentBusiness(business));
    setIsDetailModalVisible(true);
  };
  const showBusinessEditorModal = (business: IBusinessFull) => {
    dispatch(setCurrentBusiness(business));
    setIsEditorModalVisible(true);
  };
  const showBusinessFilesModal = (business: IBusinessFull) => {
    dispatch(setCurrentBusiness(business));
    setIsFilesModalVisible(true);
  };
  const showBusinessDevicesModal = (business: IBusinessFull) => {
    dispatch(setCurrentBusiness(business));
    // The list payload carries only devices[].lastOnline (PC-797 trim), so
    // refetch the full business by id to give the modal complete device records.
    dispatch(getBusinessThunk(business._id));
    setIsDevicesModalVisible(true);
  };

  const openMerchantHub = (business: IBusinessFull) => {
    if (!idToken) return;
    const url = new URL(WEB_PORTAL.BASE_URL);
    url.searchParams.set("adminIdToken", idToken);
    url.searchParams.set("merchantId", business._id);
    if (user?.email) url.searchParams.set("adminEmail", user.email);
    if (user?.name) url.searchParams.set("adminName", user.name);
    url.searchParams.set("source", "klearly-admin");
    window.open(url.toString(), "_blank", "noopener,noreferrer");
  };

  const showApprovalModal = (business: IBusinessFull) => {
    dispatch(setCurrentBusiness(business));
    setIsApprovalModalVisible(true);
  };
  const showDeclinedModal = (business: IBusinessFull) => {
    dispatch(setCurrentBusiness(business));
    setIsDeclinedModalVisible(true);
  };
  const showSubmitModal = (business: IBusinessFull) => {
    dispatch(setCurrentBusiness(business));
    setIsSubmitModalVisible(true);
  };

  const debouncedNameFilterChange = useMemo(
    () => debounce((value: string) => handleNameFilterChange(value), 400),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleNameFilterChange = (rawValue: string) => {
    const name = rawValue;
    // dispatch(filterTableByName(name));
    setFilters((prev) => {
      if (!name || name === "") {
        delete prev.name;

        return { ...prev };
      } else {
        let updatedFilters = {
          ...prev,
          name: name.replace(/\s+/g, " ").trim(),
        };
        return { ...updatedFilters };
      }
    });
    setTableParams((prev) => {
      return { ...prev, pagination: { ...prev.pagination, current: 1 } };
    });
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IBusinessFull> | SorterResult<IBusinessFull>[],
    extra: TableCurrentDataSource<IBusinessFull>
  ) => {
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const order = activeSorter?.order ?? undefined;
    const rawField = order
      ? String(activeSorter?.columnKey ?? activeSorter?.field)
      : undefined;
    const field =
      rawField && isBusinessSortField(rawField) ? rawField : undefined;
    // A sort change jumps back to page 1 so the top of the re-ordered list is
    // shown; a size change moves to the page that still holds the first row
    // the user was looking at; a plain page change keeps the page.
    const previous = tableParams.pagination;
    const sizeChanged =
      !!previous?.current &&
      !!previous?.pageSize &&
      pagination.pageSize !== previous.pageSize;
    let nextPagination = pagination;
    if (extra.action === "sort") {
      nextPagination = { ...pagination, current: 1 };
    } else if (sizeChanged) {
      nextPagination = {
        ...pagination,
        current: pageAfterSizeChange(
          { current: previous.current!, pageSize: previous.pageSize! },
          pagination.pageSize!
        ),
      };
    }
    setTableParams({
      pagination: nextPagination,
      sortField: field,
      sortOrder: order ?? undefined,
    });
  };

  const getOnboardingFlagsList = (
    onboardingFlags: IOnboardingFlag[],
    business?: IBusinessFull
  ) => {
    return (
      <List
        // header={<div>Header</div>}
        // footer={<div>Footer</div>}
        bordered
        dataSource={onboardingFlags}
        renderItem={(item) => (
          <>
            <List.Item>
              <Typography.Text mark>[{item.type}]</Typography.Text>{" "}
              {item.message}
            </List.Item>
            {business?.payoutOptions?.schedule !== "daily" &&
              onboardingFlags.find(
                (f) => f.type === "PAYOUT_SCHEDULE_CHANGE"
              ) === null && (
                <List.Item>
                  <Typography.Text mark>
                    ["PAYOUT_SCHEDULE_CHANGE"]
                  </Typography.Text>{" "}
                  {item.message}
                </List.Item>
              )}
          </>
        )}
      />
    );
  };

  const renderAcquirerColumn = (
    kind: "rapydValitor" | "rapydNew" | "shift4" | "adyen",
    record: IBusinessFull,
    extras?: { tooltip?: React.ReactNode; actions?: React.ReactNode }
  ) => {
    const resolved = resolveAcquirerColumnStatus(kind, record);
    return (
      <AcquirerStatusCell
        view={statusView}
        {...resolved}
        tooltip={extras?.tooltip}
        actions={statusView === "dots" ? undefined : extras?.actions}
      />
    );
  };

  const columns: TableProps<IBusinessFull>["columns"] = [
    {
      title: "",
      key: "onlineStatus",
      width: 24,
      align: "center" as const,
      render: (_: unknown, business: IBusinessFull) => {
        const activeDeviceCount = getActiveDeviceCount(business);
        const hasActiveDevices = activeDeviceCount > 0;
        return (
          <Tooltip
            title={`${activeDeviceCount} active device${activeDeviceCount === 1 ? "" : "s"}`}
          >
            <span
              className={
                hasActiveDevices
                  ? styles.active_device_indicator
                  : styles.inactive_device_indicator
              }
            />
          </Tooltip>
        );
      },
    },
    {
      title: "Business Name",
      key: "businessName",
      width: 101,
      render: (_: unknown, business: IBusinessFull) => (
        <Space
          direction="vertical"
          size={2}
          style={{ width: "100%", minWidth: 0 }}
        >
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              columnGap: 6,
              rowGap: 4,
              fontWeight: 600,
              fontSize: token.fontSize,
            }}
          >
            <span
              title={business.businessName}
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                overflowWrap: "anywhere",
                maxWidth: "100%",
              }}
            >
              <CountryFlag
                alpha2={resolveCountryAlpha2(business?.address?.country)}
                style={{ marginRight: 6 }}
              />
              {business.businessName}
            </span>
            {business?.complianceContact?.status ===
              ComplianceStatus.ReachedOut && (
              <Tag
                icon={<CheckOutlined />}
                style={{
                  margin: 0,
                  backgroundColor: "#f97316",
                  borderColor: "#f97316",
                  color: "#fff",
                  fontSize: 10,
                  lineHeight: "18px",
                  padding: "0 7px",
                  borderRadius: 7,
                }}
              >
                Reached out
              </Tag>
            )}
          </div>
          {business?.businessTradeName &&
            business?.businessTradeName !== business?.businessName && (
              <div
                title={business.businessTradeName}
                style={{
                  fontSize: token.fontSizeSM,
                  color: token.colorTextSecondary,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                © {business.businessTradeName}
              </div>
            )}
          {business.businessRegistrationNumber && (
            <div
              style={{
                fontSize: token.fontSizeSM,
                color: token.colorTextSecondary,
              }}
            >
              {business.businessRegistrationNumber}
            </div>
          )}
        </Space>
      ),
    },
    {
      title: "Owner Details",
      key: "ownerDetails",
      width: 100,
      render: (_: unknown, record: IBusinessFull) => (
        <Space
          direction="vertical"
          size={2}
          style={{ width: "100%", minWidth: 0 }}
        >
          <div
            title={record.rootUser?.name}
            style={{
              color: token.colorPrimary,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {record.rootUser?.name}
          </div>
          <div
            title={record.rootUser?.email}
            style={{
              fontSize: token.fontSizeSM,
              color: token.colorTextSecondary,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {record.rootUser?.email}
          </div>
        </Space>
      ),
    },
    {
      title: "Valitor",
      key: "acquirerRapydValitor",
      width: 125,
      render: (_: unknown, record: IBusinessFull) => {
        const isPendingVerification = record.status === "pendingVerification";
        const isPendingSubmission = record.status === "pendingSubmission";
        return renderAcquirerColumn("rapydValitor", record, {
          actions: (
            <>
              {isPendingVerification && (
                <span className={styles.status_actions}>
                  <Tooltip title="Approve">
                    <CheckCircleOutlined
                      style={{
                        color: "green",
                        cursor: "pointer",
                        fontSize: 14,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        showApprovalModal(record);
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Decline">
                    <CloseOutlined
                      style={{
                        color: "red",
                        cursor: "pointer",
                        fontSize: 12,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        showDeclinedModal(record);
                      }}
                    />
                  </Tooltip>
                </span>
              )}
              {isPendingSubmission && (
                <span className={styles.status_actions}>
                  <Tooltip
                    title={`Submit to Rapyd${record.externalOnboardingId ? "" : " (Old)"}`}
                  >
                    <PlusCircleOutlined
                      style={{
                        color: "#5b4cfc",
                        cursor: "pointer",
                        fontSize: 16,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        showSubmitModal(record);
                      }}
                    />
                  </Tooltip>
                  <Tooltip title="Decline">
                    <CloseOutlined
                      style={{
                        color: "red",
                        cursor: "pointer",
                        fontSize: 12,
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        showDeclinedModal(record);
                      }}
                    />
                  </Tooltip>
                </span>
              )}
            </>
          ),
        });
      },
    },
    ...(!hideUnreleasedAcquirers
      ? [
          {
            title: "Rapyd",
            key: "acquirerRapydNew",
            width: 95,
            render: (_: unknown, record: IBusinessFull) =>
              renderAcquirerColumn("rapydNew", record),
          },
        ]
      : []),
    ...(!hideUnreleasedAcquirers
      ? [
          {
            title: "Shift4",
            key: "acquirerShift4",
            width: 122,
            render: (_: unknown, record: IBusinessFull) => {
              const shift4Status = record.shift4?.onboardingStatus;
              const canSubmitShift4 =
                !shift4Status || shift4Status === "failed";
              const shift4FailureReason =
                shift4Status === "failed" || shift4Status === "rejected"
                  ? record.shift4?.lastFailureReason
                  : undefined;
              return renderAcquirerColumn("shift4", record, {
                tooltip: shift4FailureReason,
                actions: canSubmitShift4 ? (
                  <Tooltip title="Submit to Shift4">
                    <PlusCircleOutlined
                      style={{
                        color: "#5b4cfc",
                        cursor: "pointer",
                        fontSize: 14,
                      }}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        setShift4SubmitBusiness(record);
                        setIsShift4SubmitModalVisible(true);
                      }}
                    />
                  </Tooltip>
                ) : undefined,
              });
            },
          },
        ]
      : []),
    {
      title: "Adyen",
      key: "acquirerAdyen",
      width: 122,
      render: (_: unknown, record: IBusinessFull) => {
        const adyenStatus = record.adyen?.onboardingStatus;
        const isOnboardingDisallowed = adyenStatus === "onboardingDisallowed";
        const adyenFlags = record.acquirerOnboardingFlags?.adyen ?? [];
        const canSubmitOnline = adyenStatus === "onboardingAllowed";
        const canCopyMerchantHubLink =
          adyenStatus !== undefined &&
          COPY_ELIGIBLE_ADYEN_STATUSES.has(adyenStatus);
        const pricingTooltip = isOnboardingDisallowed ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {adyenFlags.map((flag, i) => (
              <div key={`${flag.type}-${i}`}>{flag.message}</div>
            ))}
          </div>
        ) : undefined;
        return renderAcquirerColumn("adyen", record, {
          tooltip: pricingTooltip,
          actions: (
            <>
              {canSubmitOnline && (
                <Tooltip title="Submit to Adyen">
                  <PlusCircleOutlined
                    style={{
                      color: "#5b4cfc",
                      cursor: "pointer",
                      fontSize: 14,
                    }}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      setAdyenSubmitBusiness(record);
                      setIsAdyenSubmitModalVisible(true);
                    }}
                  />
                </Tooltip>
              )}
              {canCopyMerchantHubLink && (
                <Tooltip title="Copy Merchant Hub onboarding link">
                  <CopyOutlined
                    style={{
                      color: "#5b4cfc",
                      cursor: "pointer",
                      fontSize: 14,
                    }}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      handleCopyMerchantHubLink(record);
                    }}
                  />
                </Tooltip>
              )}
            </>
          ),
        });
      },
    },
    {
      title: "Capabilities",
      dataIndex: "features",
      key: "features",
      width: 84,
      render: (features: BusinessFeatureFilter[] | undefined) => {
        if (!features?.length) return "-";
        return (
          <Space direction="vertical" size={4}>
            {features.map((feature) => {
              const props = featureTagProps[feature];
              if (!props) return null;
              return (
                <Tag
                  key={feature}
                  style={{
                    backgroundColor: props.backgroundColor,
                    borderColor: props.borderColor,
                    color: props.textColor,
                    margin: 0,
                    fontSize: 10,
                    lineHeight: "18px",
                    padding: "0 4px",
                    borderRadius: 10,
                  }}
                >
                  {props.label}
                </Tag>
              );
            })}
          </Space>
        );
      },
    },
    {
      title: "Partner",
      dataIndex: "partnerId",
      key: "partnerId",
      width: 52,
      render: (partner: IPartner) => (
        <Typography.Text>{partner?.name}</Typography.Text>
      ),
    },
    {
      title: "Created",
      dataIndex: BusinessSortField.CreatedAt,
      key: BusinessSortField.CreatedAt,
      width: 68,
      sorter: sortableFields.includes(BusinessSortField.CreatedAt),
      sortOrder:
        tableParams.sortField === BusinessSortField.CreatedAt
          ? (tableParams.sortOrder as SortOrder)
          : undefined,
      render: (value: string) =>
        value ? dayjs(value).format("DD MMM YYYY") : "-",
    },
    {
      title: "Flags",
      key: "flags",
      width: 46,
      align: "center" as const,
      render: (_: unknown, record: IBusinessFull) => {
        // Shift4's rejection reasons land in its own bucket; while the
        // integration is unreleased they stay out of the badge entirely.
        const acquirerFlags = [
          ...(record.acquirerOnboardingFlags?.adyen ?? []),
          ...(hideUnreleasedAcquirers
            ? []
            : (record.acquirerOnboardingFlags?.shift4 ?? [])),
        ];
        const legacyFlags = (record.onboardingFlags ?? []).filter(
          (f) => !f.isResolved
        );
        const merged = mergeOnboardingFlags(acquirerFlags, legacyFlags);

        if (merged.length === 0) return null;

        return (
          <Popover
            content={
              <OnboardingFlagsList flags={merged} persons={record.persons} />
            }
            title="Onboarding Flags"
          >
            <Badge count={merged.length}>
              <FlagOutlined style={{ color: "red", fontSize: "18px" }} />
            </Badge>
          </Popover>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      align: "center" as const,
      render: (_: any, record: IBusinessFull) => {
        const menuItems: MenuProps["items"] = [
          {
            key: "details",
            icon: <InfoCircleOutlined />,
            label: "Info",
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              showBusinessDetailsModal(record);
            },
          },
          {
            key: "edit",
            icon: <EditOutlined />,
            label: "Edit",
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              showBusinessEditorModal(record);
            },
          },
          ...(canAccessMerchantHub && idToken
            ? [
                {
                  key: "hub",
                  icon: <ExportOutlined />,
                  label: "Merchant Hub",
                  onClick: ({ domEvent }: any) => {
                    domEvent.stopPropagation();
                    openMerchantHub(record);
                  },
                },
              ]
            : []),
          {
            key: "files",
            icon: <FileOutlined />,
            label: "Files",
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              showBusinessFilesModal(record);
            },
          },
          {
            key: "devices",
            icon: <HddOutlined />,
            label: "Devices",
            onClick: ({ domEvent }) => {
              domEvent.stopPropagation();
              showBusinessDevicesModal(record);
            },
          },
          ...(extraRowActions ?? []).map((action) => ({
            key: action.key,
            icon: action.icon,
            label: action.label,
            onClick: ({ domEvent }: any) => {
              domEvent.stopPropagation();
              action.onClick(record);
            },
          })),
        ];

        return (
          <Space size={1} align="center" onClick={(e) => e.stopPropagation()}>
            <Tooltip title="Details">
              <Button
                type="text"
                size="small"
                icon={<InfoCircleOutlined style={{ fontSize: 13 }} />}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 22,
                  height: 22,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  showBusinessDetailsModal(record);
                }}
              />
            </Tooltip>
            <Tooltip title="Edit">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined style={{ fontSize: 13 }} />}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 22,
                  height: 22,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  showBusinessEditorModal(record);
                }}
              />
            </Tooltip>
            <Tooltip title="View Devices">
              <Button
                type="text"
                size="small"
                icon={<DesktopOutlined style={{ fontSize: 13 }} />}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 22,
                  height: 22,
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  showBusinessDevicesModal(record);
                }}
              />
            </Tooltip>
            <Dropdown
              menu={{ items: menuItems }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <Button
                type="text"
                size="small"
                icon={<MoreOutlined style={{ fontSize: 15 }} />}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 22,
                  height: 22,
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </Dropdown>
          </Space>
        );
      },
    },
  ];

  const updateColumnPrefs = (next: BusinessColumnPrefs) => {
    setColumnPrefs(next);
    saveBusinessColumnPrefs(next);
  };

  const handleColumnOrderChange = (order: string[]) =>
    updateColumnPrefs({ ...columnPrefs, order });

  const handleColumnVisibilityChange = (key: string, visible: boolean) =>
    updateColumnPrefs({
      ...columnPrefs,
      hidden: visible
        ? columnPrefs.hidden.filter((hiddenKey) => hiddenKey !== key)
        : Array.from(new Set([...columnPrefs.hidden, key])),
    });

  const handleColumnPrefsReset = () => {
    clearBusinessColumnPrefs();
    setColumnPrefs(EMPTY_BUSINESS_COLUMN_PREFS);
  };

  const columnOptions = buildBusinessColumnOptions(columns, columnPrefs);
  const visibleColumns = applyBusinessColumnPrefs(columns, columnPrefs);

  return (
    <div className={styles.container}>
      <div className={styles.layout}>
        <BusinessTableFilters
          filters={filters}
          setFilters={setFilters}
          setTableParams={setTableParams}
          onReset={() => {
            debouncedNameFilterChange.cancel();
            setSearchValue("");
            setFilters((prev) => {
              const { name: _name, ...rest } = prev;
              return rest;
            });
            setTableParams((prev) => ({
              ...prev,
              pagination: { ...prev.pagination, current: 1 },
            }));
          }}
        />
        <div className={styles.table_section}>
          <div className={styles.search_container}>
            <div
              ref={searchInputWrapperRef}
              className={styles.search_input_wrap}
            >
              <Popover
                open={searchHintOpen}
                content={<SearchHintContent />}
                placement="bottomLeft"
                trigger={[]}
                overlayStyle={
                  searchHintWidth ? { width: searchHintWidth } : undefined
                }
              >
                <Input
                  value={searchValue}
                  onFocus={openSearchHint}
                  onBlur={() => setSearchHintOpen(false)}
                  onChange={(e) => {
                    setSearchHintOpen(false);
                    setSearchValue(e.target.value);
                    debouncedNameFilterChange(e.target.value);
                  }}
                  onPressEnter={(e) => {
                    debouncedNameFilterChange.cancel();
                    setSearchHintOpen(false);
                    handleNameFilterChange(e.currentTarget.value);
                  }}
                  placeholder="Search businesses — name, KVK, MID, email, or sn:30025551"
                  className={styles.searchInput}
                  prefix={<SearchOutlined />}
                />
              </Popover>
            </div>
          </div>
          <div className={styles.table_panel}>
            <div className={styles.view_card}>
              <div className={styles.view_toggle}>
                <span className={styles.view_label}>Acquirer status</span>
                <Segmented
                  value={statusView}
                  onChange={(value) =>
                    setStatusView(value as AcquirerStatusView)
                  }
                  options={[
                    { label: "Labels", value: "labels", title: "" },
                    { label: "Dots only", value: "dots", title: "" },
                  ]}
                />
              </div>
              <BusinessColumnSettings
                options={columnOptions}
                onOrderChange={handleColumnOrderChange}
                onVisibilityChange={handleColumnVisibilityChange}
                onReset={handleColumnPrefsReset}
              />
            </div>
            <Table
              dataSource={businessesInTable}
              columns={visibleColumns}
              pagination={{
                ...tableParams.pagination,
                // Hidden while every result fits on one page at the smallest
                // size — no option would change what is shown.
                showSizeChanger:
                  (tableParams.pagination?.total ?? 0) >
                  BUSINESS_PAGE_SIZE_OPTIONS[0],
                pageSizeOptions: BUSINESS_PAGE_SIZE_OPTIONS,
              }}
              onChange={handleTableChange}
              loading={loading}
              size="middle"
              // Fixed layout makes the widths below proportions of the
              // container, so the whole grid fits without sideways scrolling.
              tableLayout="fixed"
              style={{ width: "100%" }}
            />
          </div>
        </div>
      </div>

      <BusinessDetailsModal
        visible={isDetailModalVisible}
        setVisible={setIsDetailModalVisible}
      ></BusinessDetailsModal>
      <BusinessEditorModal
        visible={isEditorModalVisible}
        setVisible={setIsEditorModalVisible}
        focusWebsiteSignal={websiteFocusSignal}
      ></BusinessEditorModal>
      <BusinessFilesModal
        visible={isFilesModalVisible}
        setVisible={setIsFilesModalVisible}
      ></BusinessFilesModal>
      <BusinessDevicesModal
        visible={isDevicesModalVisible}
        setVisible={setIsDevicesModalVisible}
      ></BusinessDevicesModal>

      <SubmitModal
        visible={isSubmitModalVisible}
        setVisible={setIsSubmitModalVisible}
      />
      <ApprovalModal
        visible={isApprovalModalVisible}
        setVisible={setIsApprovalModalVisible}
      />
      <DeclinedModal
        visible={isDeclinedModalVisible}
        setVisible={setIsDeclinedModalVisible}
      />
      <PendingVerificationModal
        visible={isPendingVerificationModalVisible}
        setVisible={setIsPendingVerificationdModalVisible}
      />
      <Shift4SubmitModal
        visible={isShift4SubmitModalVisible}
        business={shift4SubmitBusiness}
        onClose={() => {
          setIsShift4SubmitModalVisible(false);
          setShift4SubmitBusiness(null);
        }}
        onSubmitted={(businessId) => {
          setBusinessesInTable((prev: any[]) =>
            prev.map((b) =>
              b._id === businessId
                ? {
                    ...b,
                    shift4: {
                      ...b.shift4,
                      onboardingStatus: "submitted",
                      lastFailureReason: undefined,
                    },
                  }
                : b
            )
          );
        }}
      />
      <AdyenSubmitModal
        visible={isAdyenSubmitModalVisible}
        business={adyenSubmitBusiness}
        onClose={() => {
          setIsAdyenSubmitModalVisible(false);
          setAdyenSubmitBusiness(null);
        }}
        onSubmitted={(businessId, onboardingLink) => {
          setBusinessesInTable((prev: any[]) =>
            prev.map((b) =>
              b._id === businessId
                ? {
                    ...b,
                    adyen: {
                      ...b.adyen,
                      onboardingStatus: "initiated",
                      onboardingLink,
                    },
                  }
                : b
            )
          );
        }}
        onAddWebsite={(business) => {
          showBusinessEditorModal(business);
          setWebsiteFocusSignal((n) => n + 1);
        }}
      />
    </div>
  );
}
